import { useEffect, useState } from "react";
import { LayoutDashboard, Loader2, Plus, IndianRupee, CalendarClock } from "lucide-react";
import { DashShell, StatCard, Badge } from "@/components/dash/DashShell";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, inr, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const NAV = [{ to: "/organiser", label: "Dashboard", icon: LayoutDashboard }];
const KINDS = [["events", "Event / Seminar / Webinar"], ["podcasts", "Podcast"], ["opportunities", "Opportunity"]];

export default function OrganiserDashboard() {
    const [stats, setStats] = useState(null);
    const [listings, setListings] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [open, setOpen] = useState(false);
    const [f, setF] = useState({ kind: "events", fee: 0 });
    const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

    const load = () => {
        api.get("/organiser/stats").then(({ data }) => setStats(data));
        api.get("/organiser/listings").then(({ data }) => setListings(data));
        api.get("/organiser/bookings").then(({ data }) => setBookings(data));
    };
    useEffect(load, []);

    const create = async (e) => {
        e.preventDefault();
        if (!f.title || !f.category || !f.about) { toast.error("Title, category and description are required"); return; }
        try {
            await api.post("/organiser/listings", { kind: f.kind, title: f.title, category: f.category, location: f.location || "Online", date: f.date || "Rolling", fee: Number(f.fee) || 0, about: f.about, audience: f.audience || "", image: f.image || "" });
            toast.success("Listing published"); setOpen(false); setF({ kind: "events", fee: 0 }); load();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };

    const patchBooking = async (id, status) => {
        try { await api.patch(`/bookings/${id}`, { status }); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    return (
        <DashShell nav={NAV} title="Organiser Dashboard">
            {!stats ? <div className="grid place-items-center py-32 text-teal-900/40"><Loader2 className="w-7 h-7 animate-spin" /></div> : (
                <div className="space-y-6">
                    <div className="flex justify-end">
                        <Dialog open={open} onOpenChange={setOpen}>
                            <DialogTrigger asChild><button className="btn-gold rounded-full px-5 py-2.5 text-sm flex items-center gap-2" data-testid="create-listing-btn"><Plus className="w-4 h-4" /> Create Listing</button></DialogTrigger>
                            <DialogContent className="max-w-lg">
                                <DialogHeader><DialogTitle className="font-serif text-xl">Create a Listing</DialogTitle></DialogHeader>
                                <form onSubmit={create} className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
                                    <Fld label="Type"><select className="w-full h-9 rounded-md border border-input bg-white px-3 text-sm" value={f.kind} onChange={set("kind")} data-testid="listing-kind">{KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Fld>
                                    <Fld label="Title *"><Input value={f.title || ""} onChange={set("title")} data-testid="listing-title" /></Fld>
                                    <div className="grid grid-cols-2 gap-3">
                                        <Fld label="Category *"><Input value={f.category || ""} onChange={set("category")} placeholder="e.g. NETWORKING" data-testid="listing-category" /></Fld>
                                        <Fld label="Location"><Input value={f.location || ""} onChange={set("location")} data-testid="listing-location" /></Fld>
                                        <Fld label="Date"><Input value={f.date || ""} onChange={set("date")} placeholder="2026-12-01" data-testid="listing-date" /></Fld>
                                        <Fld label="Fee (₹)"><Input type="number" value={f.fee} onChange={set("fee")} data-testid="listing-fee" /></Fld>
                                        <Fld label="Audience"><Input value={f.audience || ""} onChange={set("audience")} data-testid="listing-audience" /></Fld>
                                        <Fld label="Image URL"><Input value={f.image || ""} onChange={set("image")} /></Fld>
                                    </div>
                                    <Fld label="Description *"><Textarea rows={3} value={f.about || ""} onChange={set("about")} data-testid="listing-about" /></Fld>
                                    <button type="submit" className="btn-gold w-full rounded-full py-2.5 text-sm" data-testid="listing-submit">Publish Listing</button>
                                </form>
                            </DialogContent>
                        </Dialog>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <StatCard label="Listings" value={stats.listings} icon={LayoutDashboard} />
                        <StatCard label="Bookings" value={stats.bookings} icon={CalendarClock} accent="gold" />
                        <StatCard label="Revenue (90%)" value={inr(stats.revenue)} icon={IndianRupee} accent="green" />
                        <StatCard label="SuDigi Commission" value={inr(stats.commission)} icon={IndianRupee} accent="red" />
                    </div>

                    <Panel title="My Listings">
                        {listings.length === 0 ? <Empty text="No listings yet. Create your first listing." /> : (
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {listings.map((l) => (
                                    <div key={l.slug} className="border border-black/5 rounded-xl overflow-hidden" data-testid={`listing-${l.slug}`}>
                                        <img src={l.image} alt={l.title} className="h-28 w-full object-cover" />
                                        <div className="p-3"><div className="text-xs text-teal-900/50 uppercase">{l.kind} · {l.category}</div><div className="font-semibold text-teal-900 text-sm mt-0.5">{l.title}</div><div className="font-mono text-sm mt-1">{l.fee ? inr(l.fee) : "Free"}</div></div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Panel>

                    <Panel title="Bookings">
                        {bookings.length === 0 ? <Empty text="No bookings yet." /> : (
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[640px] text-sm">
                                    <thead className="bg-teal-900/5 text-teal-900/60 text-xs uppercase"><tr>{["Booking", "Customer", "Listing", "Amount", "You get (90%)", "Status", "Action"].map((h) => <th key={h} className="text-left px-3 py-2 font-medium">{h}</th>)}</tr></thead>
                                    <tbody className="divide-y divide-black/5">
                                        {bookings.map((b) => (
                                            <tr key={b.booking_id} data-testid={`obk-${b.booking_id}`}>
                                                <td className="px-3 py-2 font-mono text-xs text-teal-700">{b.booking_id}</td>
                                                <td className="px-3 py-2">{b.customer_name}</td>
                                                <td className="px-3 py-2">{b.title}</td>
                                                <td className="px-3 py-2 font-mono">{inr(b.amount)}</td>
                                                <td className="px-3 py-2 font-mono text-growth">{inr(b.provider_amount)}</td>
                                                <td className="px-3 py-2"><Badge status={b.status} /></td>
                                                <td className="px-3 py-2">{b.status === "Pending" && <button onClick={() => patchBooking(b.booking_id, "Confirmed")} className="text-xs btn-gold rounded-full px-3 py-1" data-testid={`confirm-${b.booking_id}`}>Confirm</button>}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Panel>
                </div>
            )}
        </DashShell>
    );
}
function Panel({ title, children }) { return <div className="bg-white rounded-2xl border border-black/5 p-5 shadow-sm"><h3 className="font-semibold text-teal-900 mb-4">{title}</h3>{children}</div>; }
function Fld({ label, children }) { return <div className="space-y-1.5"><Label className="text-sm">{label}</Label>{children}</div>; }
function Empty({ text }) { return <p className="text-teal-900/40 text-sm py-6 text-center">{text}</p>; }
